import { CardFlavours } from '@components/Card2/types';
import QRCode from '@components/QRCode/QRCode';
import Toggle from '@components/Toggle/Toggle';
import Tooltip from '@components/Tooltip/Tooltip';
import { cardEditContext } from '@context/card-edit-context';
import { faCrown, faLock } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { CardDetailsFragment } from '@graphql/fragments/cards/core.graphql';
import { useActorPlan } from '@hooks/actor/use-actor-plan';
import { useCard } from '@hooks/card';
import { useCardImageMutations } from '@hooks/card/image';
import { useCardFlagMutations } from '@hooks/card/use-card-flags';
import { useCardAttribute } from '@hooks/use-card-attributes';
import { analytics } from '@modules/analytics';
import { useGetQRCodeUrl } from '@modules/card-urls/use-get-qr-card-url';
import { useFeatureFlag } from '@modules/feature-flag';
import { ModalType } from '@modules/global-modals/types';
import { useGlobalModal } from '@modules/global-modals/use-global-modal';
import { Alert, Box, Heading, Inline, Stack, Text } from '@ui-lib';
import type { Maybe } from 'graphql/jsutils/Maybe';
import { AnimatePresence, motion } from 'motion/react';
import { useContext, useEffect, useMemo, useState } from 'react';

import { useBlockAttributes } from '../../hooks/use-block-attribibute';
import { crownStyles, qrCodeSettingsToggles } from './QRCodeSection.css';
import { QRCodeSectionImageEditor } from './QRCodeSectionImageEditor';
import type { CardImage } from '.cache/__types__';

interface QRCodeSectionProps {
  parentCard: CardDetailsFragment;
  draftCard?: Maybe<CardDetailsFragment>;
  qrCodeImage: Partial<CardImage> | undefined | null;
  companyLogoImage: Partial<CardImage> | undefined | null;
}

const QR_CODE_SIZE = 380;

export const QRCodeSection: React.FC<QRCodeSectionProps> = ({
  parentCard,
  draftCard,
  qrCodeImage,
  companyLogoImage,
}) => {
  const card = draftCard ?? parentCard;

  const useCardLogoInQrCode = card?.flags?.use_card_logo_in_qr ?? false;
  const { planType, loading } = useActorPlan();
  const { openModal } = useGlobalModal({
    registeredModals: ['UPGRADE'],
  });
  const [variant, { loading: loadingFlag }] = useFeatureFlag('blinq_logo_in_qr', 'control');
  const [previewQrCodeImage, setPreviewQrCodeImage] = useState<string | undefined>(
    qrCodeImage?.edited_url ?? '',
  );

  const { getQrCardUrl } = useGetQRCodeUrl();

  // Keep the previewQrCodeImage updated with the edited_url from the qrCodeImage
  useEffect(() => {
    if (qrCodeImage?.edited_url) {
      setPreviewQrCodeImage(qrCodeImage?.edited_url);
    }
  }, [qrCodeImage?.edited_url]);

  // Card state and mutations
  const { isAttributeBlocked, toggleBlockAttribute } = useBlockAttributes({
    categoryAllowList: ['image'],
    card,
  });
  const qrCodeImageAttribute = useCardAttribute('embedded_qr_code_image');
  const isBlocked = isAttributeBlocked(qrCodeImageAttribute?.id);

  const { updateOrCreate, remove, update } = useCardImageMutations();
  const { toggleFlag } = useCardFlagMutations();

  // We don't actually us this card, we just want to be able to refetch the card after updating a CardFlag.
  const { refetch: refetchEntireCard } = useCard(card?.id);

  // Contexts
  const { flavour } = useContext(cardEditContext);

  const isLocked = useMemo(() => {
    const isCardLinkedToTemplate = card?.template !== null;
    if (isCardLinkedToTemplate) {
      // Weird Business logic begins now: For the QR code, if your card has a template, then you can't edit the QR code, no matter what.
      // We can't always trust the image.is_locked field so acts as a back up to ensure templated cards can never edit their QR code.
      return true;
    }

    if (qrCodeImage) {
      return qrCodeImage.is_locked;
    }

    return isBlocked;
  }, [card?.template, isBlocked, qrCodeImage]);

  const canUnlock = useMemo(() => {
    if (flavour === CardFlavours.TEMPLATE) {
      return true;
    }

    const isCardLinkedToTemplate = card?.template !== null;
    if (isCardLinkedToTemplate) {
      // Weird Business logic begins now: For the QR code, if your card has a template, then you can't edit the QR code, no matter what.
      return false;
    }

    if (flavour === CardFlavours.TEAM) {
      return true;
    }

    return false;
  }, [flavour, card]);

  function updateOrCreateQRCodeImage(editedUrl: string) {
    if (isLocked) {
      return;
    }

    updateOrCreate(
      {
        id: qrCodeImage?.id,
        original_url: editedUrl,
        edited_url: editedUrl,
        instruction_text: null,
        is_optional: true,
        stage: 'done',
        value_provider: flavour === CardFlavours.TEMPLATE ? 'template' : 'member',
        card_id: card.id,
        metadata: {
          background_color: null,
          editor_version: null,
          img_position_x: null,
          img_position_y: null,
          zoom: null,
        },
      },
      'embedded_qr_code_image',
    );
  }

  async function handleToggleDisplayLogoInQr(isChecked: boolean) {
    await toggleFlag({
      cardId: card?.id,
      key: 'show_image_in_qr_code',
      value: isChecked,
      otherCardFlags: card?.flags,
    });
  }

  async function handleToggleUseCompanyLogo(isChecked: boolean) {
    if (isChecked && companyLogoImage?.edited_url) {
      await toggleFlag({
        cardId: card?.id,
        key: 'use_card_logo_in_qr',
        value: isChecked,
        otherCardFlags: card?.flags,
      });

      setPreviewQrCodeImage(companyLogoImage?.edited_url);

      // Refetch the card so the new embedded QR Code logo is in the cache
      refetchEntireCard();
      return;
    }

    if (!isChecked) {
      setPreviewQrCodeImage(undefined);

      if (qrCodeImage?.id && qrCodeImage?.card_id) {
        remove(qrCodeImage?.id, qrCodeImage?.card_id, 'embedded_qr_code_image');
      }

      await toggleFlag({
        cardId: card?.id,
        key: 'use_card_logo_in_qr',
        value: isChecked,
        otherCardFlags: card?.flags,
      });
      return;
    }
  }

  /* Will either lock or block the QR Code image depending on if there is an image or not.
   * If there is an image, set is_locked to true.
   * If there isn't an image, block the attribute.
   */
  function toggleIsLocked(isChecked: boolean) {
    // If the CardImage exists lock it there.
    if (qrCodeImage && qrCodeImage.id) {
      const { __typename, attribute_type, template, ...rest } = qrCodeImage;
      update({
        ...rest,
        id: qrCodeImage.id,
        is_locked: isChecked,
        metadata: null,
      });
      return;
    }

    // The CardImage doesn't exists, so block that attribute
    if (!qrCodeImageAttribute?.id) {
      return;
    }
    toggleBlockAttribute(qrCodeImageAttribute.id);
    return;
  }

  function handleImageDeleted() {
    if (isLocked) {
      return;
    }
    setPreviewQrCodeImage(undefined);

    if (qrCodeImage?.id && qrCodeImage?.card_id) {
      remove(qrCodeImage.id, qrCodeImage.card_id, 'embedded_qr_code_image');
    }
  }

  function freeUserClicksLogoInQRToggle() {
    analytics.track({
      name: 'displaylogoinqrcode_clicked',
    });

    openModal({
      type: 'UPGRADE',
      trackingSource: 'card-qrcodesection',
    });
  }

  return (
    <Box
      display="flex"
      width="full"
      flexDirection={{ mobile: 'column', desktop: 'row' }}
      alignItems={{ mobile: 'center', desktop: 'flexStart' }}
      gap="large"
    >
      <QRCode
        size={QR_CODE_SIZE}
        value={getQrCardUrl(parentCard?.id, { blinqSource: 'db' })}
        imageSrc={previewQrCodeImage}
        // In this experiment, API controls the showImage prop, so we don't need to gate-keep on the view
        showImage={
          (card?.flags?.show_image_in_qr_code || (planType === 'free' && variant === 'treatment')) ?? false
        }
      />

      <AnimatePresence>
        {draftCard ? (
          <Box className={qrCodeSettingsToggles} gap={'medium'}>
            {card?.template && (
              <Alert tone="warning" icon={<FontAwesomeIcon icon={faLock} />}>
                <Text>QR Code settings are set by your admin</Text>
              </Alert>
            )}
            {flavour === CardFlavours.TEMPLATE && (
              <Alert tone="info">
                <Text>QR code settings will be applied to all cards using this template</Text>
              </Alert>
            )}
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <Box display="flex" flexDirection="row" alignItems="center" justifyContent="spaceBetween">
                {planType === 'free' && !loading && !loadingFlag && (
                  <PaywalledQRCodeSettings
                    onToggle={freeUserClicksLogoInQRToggle}
                    qrExperimentVariant={variant}
                  />
                )}
                {planType !== 'free' && !loading && (
                  <>
                    <Box flexGrow={1}>Display a logo in your QR Code</Box>
                    <Toggle
                      disabled={isLocked ?? false}
                      state={card?.flags?.show_image_in_qr_code ?? false}
                      onChange={handleToggleDisplayLogoInQr}
                    />
                  </>
                )}
              </Box>
            </motion.div>

            {card?.flags?.show_image_in_qr_code && companyLogoImage?.edited_url && (
              <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                <Inline alignY="center">
                  <Box flexGrow={1}>Use company logo</Box>
                  <Toggle
                    disabled={isLocked ?? false}
                    state={useCardLogoInQrCode}
                    onChange={handleToggleUseCompanyLogo}
                  />
                </Inline>
              </motion.div>
            )}

            {card?.flags?.show_image_in_qr_code && !useCardLogoInQrCode && (
              <QRCodeSectionImageEditor
                editedImageUrl={qrCodeImage?.edited_url ?? undefined}
                isLocked={isLocked ?? false}
                onImageDeleted={handleImageDeleted}
                onPreviewImageUrlChange={setPreviewQrCodeImage}
                onSave={updateOrCreateQRCodeImage}
              />
            )}

            {flavour === CardFlavours.TEAM && canUnlock && (
              <Inline alignY="center">
                <Box flexGrow={1}>Lock edits</Box>
                <Toggle state={isLocked ?? false} onChange={toggleIsLocked} />
              </Inline>
            )}
          </Box>
        ) : (
          planType === 'free' &&
          !loading &&
          !loadingFlag && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
              <PaywalledQRCodeSettings
                onToggle={freeUserClicksLogoInQRToggle}
                qrExperimentVariant={variant}
              />
            </motion.div>
          )
        )}
      </AnimatePresence>
    </Box>
  );
};

const PaywalledQRCodeSettings: React.FC<{
  onToggle: () => void;
  qrExperimentVariant: 'control' | 'treatment';
}> = ({ onToggle, qrExperimentVariant }) => {
  return (
    <Box display={'flex'} marginTop={'small'} gap="large">
      <Stack gap="medium">
        <Inline gap={'xxsmall'}>
          <Heading weight="demibold" level={'5'}>
            {qrExperimentVariant === 'treatment'
              ? 'Customize QR code logo'
              : 'Display a logo in your QR Code'}
          </Heading>
          <FontAwesomeIcon icon={faCrown} className={crownStyles} />
        </Inline>
        {qrExperimentVariant === 'treatment' && (
          <Text tone="secondary">
            Customize or remove Blinq logo in QR&nbsp;code
            <br />
            for better branding
          </Text>
        )}
      </Stack>

      <Tooltip
        size="small"
        label="You need to be on a Blinq Premium or Blinq Business account to add your logo to the QR code"
        aria-label="QR Code Logo explainer"
      >
        <div>
          <Toggle onClick={onToggle} state={false} />
        </div>
      </Tooltip>
    </Box>
  );
};
